import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { LocalStorageService } from "./local-storage.service";
import type { WorksheetReferralNotificationFilterKey } from "../models/admissions.model";

const REFERRAL_IDS_FILTER_KEY = "referralIdsFilter";

/** Same as wa-engage-v2 — used by Engage referral filters (clinic + standalone Engage app). */
export interface NameValuePair {
  name: string;
  value: string;
}

export interface DateRangeFilter {
  startDate: Date;
  endDate: Date;
}

export interface AppliedFiltersState {
  stages: NameValuePair[];
  assignedUsers: NameValuePair[];
  channels: NameValuePair[];
  notifyTypes: NameValuePair[];
  statuses: NameValuePair[];
  dateRange: DateRangeFilter | null;
  referralIds: string[];
  types: NameValuePair[];
  tags: string[];
  hasNotifications: boolean | null;
  hasReminder: boolean | null;
  hasStarred?: 1 | 0;
  hasNewLead?: boolean | null;
  /** PHI directory boolean flags (`has_new_lead`, `has_sms`, …) from dashboard deep link or worksheet filter sheet. */
  referralNotificationFilterKeys?: string[];
}

export interface DosFilter {
  dos: Date[] | null;
  resource: NameValuePair | null;
  location: NameValuePair | null;
  /** List-clinic `filterStatus` — same values as Schedule / referrals (`EncounterStatusV2`). */
  encounterStatuses?: NameValuePair[];
}

@Injectable({
  providedIn: "root",
})
export class FilterService {
  private appliedFiltersSubject: Subject<AppliedFiltersState>;
  private appliedFilters: AppliedFiltersState;
  private dosFiltersResult: string[] = [];
  /** Worklist row with no DOS: narrow GET /referrals by clinic patient id when referral id is unknown. */
  private worklistEngagePatientId: string | null = null;
  private appliedDosFiltersSubject: Subject<DosFilter>;
  private dosFilters: DosFilter = {
    dos: null,
    resource: null,
    location: null,
    encounterStatuses: [],
  };
  filterCounts: { [key: string]: Record<string, number> | number } = {};

  /**
   * Engage toolbar "Filters" button badge: counts Advanced filters sidebar groups + list-clinic pre-filter (DOS and/or encounter status).
   * Quick activity pills, dashboard URL date range, and patient search must not increase this count.
   * Activity (notifyTypes) counts only if the user edited it in the sidebar before Apply.
   */
  private _engageToolbarFilterBadgeCount = 0;
  private _engageToolbarBadgeIncludeNotifyTypes = false;

  constructor(private localStorageService: LocalStorageService) {
    const savedIds =
      this.localStorageService.getItem<string[]>(REFERRAL_IDS_FILTER_KEY) || [];

    this.appliedFilters = {
      stages: [],
      assignedUsers: [],
      channels: [],
      notifyTypes: [],
      statuses: [],
      types: [],
      tags: [],
      dateRange: null,
      referralIds: savedIds,
      hasNotifications: null,
      hasReminder: null,
      hasStarred: 0,
      hasNewLead: null,
      referralNotificationFilterKeys: [],
    };
    this.appliedFiltersSubject = new Subject<AppliedFiltersState>();
    this.appliedDosFiltersSubject = new Subject<DosFilter>();
    this.recomputeEngageToolbarFilterBadgeCount();
  }

  getEngageToolbarFilterBadgeCount(): number {
    return this._engageToolbarFilterBadgeCount;
  }

  /**
   * Call after Engage advanced filters sidebar Apply. `includeNotifyTypesInBadge` should be true
   * only if the user changed the Activity field in that session (before Apply).
   */
  markEngageAdvancedFiltersApplied(includeNotifyTypesInBadge: boolean): void {
    this._engageToolbarBadgeIncludeNotifyTypes = includeNotifyTypesInBadge;
    this.recomputeEngageToolbarFilterBadgeCount();
  }

  /** Call after Engage quick activity pills (New / Missed / SMS / Reminders / Voicemail). */
  markEngageQuickActivityFilterUsed(): void {
    this._engageToolbarBadgeIncludeNotifyTypes = false;
    this.recomputeEngageToolbarFilterBadgeCount();
  }

  private recomputeEngageToolbarFilterBadgeCount(): void {
    const af = this.appliedFilters;
    let n = 0;
    if ((af.types || []).length) n++;
    if ((af.channels || []).length) n++;
    if ((af.statuses || []).length) n++;
    if (this._engageToolbarBadgeIncludeNotifyTypes && (af.notifyTypes || []).length) n++;
    if ((af.stages || []).length) n++;
    if ((af.tags || []).length) n++;
    if ((af.assignedUsers || []).length) n++;
    // Referral date range is set by dashboard deep links / quick flows — do not badge the toolbar icon;
    // badge reflects Advanced filters sidebar + list-clinic pre-filter (see `markEngageAdvancedFiltersApplied`).
    if (this.isEngageListClinicPreFilterApplied) {
      n++;
    }
    this._engageToolbarFilterBadgeCount = n;
  }

  get dosFilters$(): Observable<DosFilter> {
    return this.appliedDosFiltersSubject.asObservable();
  }

  setDosFiltersResult(ids: string[]): void {
    this.dosFiltersResult = ids;
  }

  getDosFiltersResult(): string[] {
    return [...this.dosFiltersResult];
  }

  clearDosFilters(): void {
    this.dosFilters = {
      dos: null,
      resource: null,
      location: null,
      encounterStatuses: [],
    };
    this.dosFiltersResult = [];
    this.appliedDosFiltersSubject.next(this.dosFilters);
    this.recomputeEngageToolbarFilterBadgeCount();
  }

  setWorklistEngagePatientIdFilter(patientId: string | null | undefined): void {
    const t = (patientId ?? "").trim();
    this.worklistEngagePatientId = t || null;
  }

  getWorklistEngagePatientIdFilter(): string | null {
    return this.worklistEngagePatientId;
  }

  getDosFilters(): DosFilter {
    const d = this.dosFilters;
    return {
      dos: d.dos?.length === 2 ? [d.dos[0], d.dos[1]] : null,
      resource: d.resource,
      location: d.location,
      encounterStatuses: [...(d.encounterStatuses || [])],
    };
  }

  setDosFilters(filters: DosFilter): void {
    this.dosFilters = {
      dos: filters.dos?.length === 2 ? [filters.dos[0], filters.dos[1]] : null,
      resource: filters.resource,
      location: filters.location,
      encounterStatuses: [...(filters.encounterStatuses || [])],
    };
    this.appliedDosFiltersSubject.next(this.dosFilters);
  }

  /** True when user picked a DOS date range (list-clinic window is explicit). */
  get isDosFiltersApplied(): boolean {
    return this.dosFilters.dos?.length === 2;
  }

  /**
   * True when Engage should run the PHI list-clinic pre-step before GET /referrals:
   * DOS range and/or encounter status (and optional resource/location on the same payload).
   */
  get isEngageListClinicPreFilterApplied(): boolean {
    return (
      this.isDosFiltersApplied ||
      (this.dosFilters.encounterStatuses?.length ?? 0) > 0
    );
  }

  get appliedFilters$(): Observable<AppliedFiltersState> {
    return this.appliedFiltersSubject.asObservable();
  }

  getAppliedStages(): NameValuePair[] {
    return [...(this.appliedFilters.stages || [])];
  }

  getAppliedAssignedTo(): NameValuePair[] {
    return [...(this.appliedFilters.assignedUsers || [])];
  }

  getAppliedChannels(): NameValuePair[] {
    return [...(this.appliedFilters.channels || [])];
  }

  getAppliedNotificationTypes(): NameValuePair[] {
    return [...(this.appliedFilters.notifyTypes || [])];
  }

  getAppliedStatuses(): NameValuePair[] {
    return [...(this.appliedFilters.statuses || [])];
  }

  getAppliedDateRange(): DateRangeFilter | null {
    const dr = this.appliedFilters.dateRange;
    return dr ? { startDate: dr.startDate, endDate: dr.endDate } : null;
  }

  getAppliedReferralIds(): string[] {
    return [...(this.appliedFilters.referralIds || [])];
  }

  getAppliedTypes(): NameValuePair[] {
    return [...(this.appliedFilters.types || [])];
  }

  getAppliedTags(): string[] {
    return [...(this.appliedFilters.tags || [])];
  }

  public applyFilters(): void {
    this.appliedFiltersSubject.next(this._cloneState(this.appliedFilters));
  }

  private persistReferralIds(): void {
    this.localStorageService.setItem<string[]>(REFERRAL_IDS_FILTER_KEY, [
      ...(this.appliedFilters.referralIds || []),
    ]);
  }

  setReferralIds(ids: string[]): void {
    this.appliedFilters.referralIds = [...(ids || [])];
    this.persistReferralIds();
  }

  addReferralId(id: string): void {
    if (!Array.isArray(this.appliedFilters.referralIds)) {
      this.appliedFilters.referralIds = [id];
    } else if (!this.appliedFilters.referralIds.includes(id)) {
      this.appliedFilters.referralIds.push(id);
    }
    this.persistReferralIds();
  }

  removeReferralId(id: string): void {
    if (!Array.isArray(this.appliedFilters.referralIds)) return;
    const idx = this.appliedFilters.referralIds.indexOf(id);
    if (idx > -1) {
      this.appliedFilters.referralIds.splice(idx, 1);
      this.persistReferralIds();
    }
  }

  setHasNotifications(hasNotifications: boolean | null): void {
    this.appliedFilters.hasNotifications = hasNotifications;
  }

  getHasNotifications(): boolean | null {
    return this.appliedFilters.hasNotifications;
  }

  setHasStarred(hasStarred: 1 | 0): void {
    this.appliedFilters.hasStarred = hasStarred;
  }

  getHasStarred(): 1 | 0 {
    return this.appliedFilters.hasStarred || 0;
  }

  setHasReminder(hasReminder: boolean | null): void {
    this.appliedFilters.hasReminder = hasReminder;
  }

  getHasReminder(): boolean | null {
    return this.appliedFilters.hasReminder;
  }

  setHasNewLead(hasNewLead: boolean | null): void {
    this.appliedFilters.hasNewLead = hasNewLead;
  }

  getHasNewLead(): boolean | null {
    return this.appliedFilters.hasNewLead;
  }

  getReferralNotificationFilterKeys(): string[] {
    return [...(this.appliedFilters.referralNotificationFilterKeys || [])];
  }

  hasQuickReferralNotificationFilter(key: WorksheetReferralNotificationFilterKey): boolean {
    return this.getReferralNotificationFilterKeys().includes(key);
  }

  /** Quick activity pills: `has_new_lead`, `has_missed`, `has_sms`, `has_voicemail`, `has_reminder` (not `notification_type`). */
  applyQuickReferralNotificationFilter(key: WorksheetReferralNotificationFilterKey): void {
    const c = this.getCurrentFilters();
    c.notifyTypes = [];
    c.stages = [];
    c.hasNewLead = null;
    c.hasReminder = null;
    c.hasStarred = 0;
    c.hasNotifications = null;
    c.referralNotificationFilterKeys = [key];
    this.setAllFilters(c);
    this.applyFilters();
    this.markEngageQuickActivityFilterUsed();
  }

  hasActiveFilters(): boolean {
    const af = this.appliedFilters;
    if (
      af.hasStarred === 1 ||
      af.hasNotifications === true ||
      af.hasReminder === true ||
      af.hasNewLead === true ||
      af.dateRange != null ||
      (af.referralNotificationFilterKeys || []).length > 0
    ) {
      return true;
    }
    const lists = [
      af.stages,
      af.assignedUsers,
      af.channels,
      af.notifyTypes,
      af.statuses,
      af.types,
      af.tags,
      af.referralIds,
    ];
    if (lists.some((list) => Array.isArray(list) && list.length > 0)) {
      return true;
    }
    if ((this.worklistEngagePatientId || "").trim()) {
      return true;
    }
    return this.isEngageListClinicPreFilterApplied;
  }

  clearAll(): void {
    this.worklistEngagePatientId = null;
    this.appliedFilters = {
      stages: [],
      assignedUsers: [],
      channels: [],
      notifyTypes: [],
      statuses: [],
      dateRange: null,
      referralIds: [],
      types: [],
      tags: [],
      hasNotifications: null,
      hasReminder: null,
      hasStarred: 0,
      hasNewLead: null,
      referralNotificationFilterKeys: [],
    };
    this.persistReferralIds();
    this._engageToolbarBadgeIncludeNotifyTypes = false;
    this.recomputeEngageToolbarFilterBadgeCount();
    this.applyFilters();
  }

  getCurrentFilters(): AppliedFiltersState {
    return this._cloneState(this.appliedFilters);
  }

  setAllFilters(filters: AppliedFiltersState): void {
    this.appliedFilters = this._cloneState(filters);
    this.persistReferralIds();
  }

  private _cloneState(state: AppliedFiltersState): AppliedFiltersState {
    return {
      stages: [...(state.stages || [])],
      assignedUsers: [...(state.assignedUsers || [])],
      channels: [...(state.channels || [])],
      notifyTypes: [...(state.notifyTypes || [])],
      statuses: [...(state.statuses || [])],
      dateRange: state.dateRange ? { ...state.dateRange } : null,
      referralIds: [...(state.referralIds || [])],
      hasNotifications: state.hasNotifications || null,
      hasReminder: state.hasReminder || null,
      types: [...(state.types || [])],
      tags: [...(state.tags || [])],
      hasStarred: state.hasStarred || 0,
      hasNewLead: state.hasNewLead || null,
      referralNotificationFilterKeys: [...(state.referralNotificationFilterKeys || [])],
    };
  }
}
